import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../interfaces/user.interface';
import { AuthService } from '../../features/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private readonly baseUrl = environment.BASE_URL;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  // =================== USERS ENDPOINTS ===================

  /**
   * GET /users - Lista todos los usuarios
   * @param tenantId ID del tenant (requerido)
   */
  getAllUsers(tenantId: string): Observable<User[]> {
    const params = new HttpParams().set('tenantId', tenantId);
    return this.http.get<User[]>(`${this.baseUrl}/users`, { params });
  }

  /**
   * GET /users/{userId}/roles - Obtiene los roles de un usuario específico
   */
  getUserRoles(userId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/users/${userId}/roles`);
  }

  /**
   * PUT /users/{userId}/status - Actualiza el estado de un usuario
   */
  updateUserStatus(userId: string, status: { isActive: boolean; reason?: string }): Observable<User> {
    return this.http.put<User>(`${this.baseUrl}/users/${userId}/status`, status);
  }

  // =================== LEGACY METHODS (mantenidos para compatibilidad) ===================

  /**
   * Get user by userId (método legacy)
   */
  getUserById(tenantId: string, userId: string): Observable<any> {
    const params = new HttpParams()
      .set('tenantId', tenantId)
      .set('userId', userId);
    return this.http.get<User[]>(`${this.baseUrl}/users`, { params });
  }

  /**
   * Create new user (método legacy)
   */
  createUser(userData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/users`, userData);
  }

  /**
   * Update user (método legacy)
   */
  updateUser(userId: string, userData: any): Observable<any> {
    // Obtener el tenantId del servicio de autenticación
    const tenantId = this.authService.getCurrentTenantId();
    
    // Incluir tenantId en el payload junto con los campos nuevos
    const payload = {
      ...userData,
      tenantId: tenantId
    };
    return this.http.put<any>(`${this.baseUrl}/users/${userId}`, payload);
  }

  /**
   * Delete user (método legacy)
   */
  deleteUser(username: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}users/${username}`);
  }

  /**
   * Change password (método legacy)
   */
  changePassword({username, password}: {username: string, password: string}): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}`, { username, password });
  }

  /**
   * Get users by confirmation status (método legacy)
   */
  getUsersByConfirm(paginationToken?: string | null): Observable<any> {
    const body = {
      paginationToken: paginationToken || null
    };
    return this.http.post<any>(`${this.baseUrl}users/by-confirm`, body);
  }

  /**
   * Get confirmed users (método legacy)
   */
  getConfirmedUsers(paginationToken?: string | null): Observable<any> {
    const body = {
      paginationToken: paginationToken || null
    };
    return this.http.post<any>(`${this.baseUrl}users/confirmed`, body);
  }

  /**
   * Add user to group (método legacy)
   */
  addUserToGroup(groupData: { username: string; group: string }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}users/groups`, groupData);
  }

  /**
   * Remove user from group (método legacy)
   */
  removeUserFromGroup(username: string, group: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}users/groups/${username}/${group}`);
  }

  /**
   * Get user groups (método legacy)
   */
  getUserGroups(username: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}users/groups/${username}`);
  }

  // =================== GROUP MANAGEMENT METHODS (legacy) ===================

  /**
   * Get all groups (método legacy)
   */
  getAllGroups(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}groups/all`);
  }

  /**
   * Create group (método legacy)
   */
  createGroup(groupData: { group: string; description: string; precedence?: number }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}groups`, groupData);
  }

  /**
   * Update group (método legacy)
   */
  updateGroup(groupData: { group: string; description: string; precedence?: number }): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}groups`, groupData);
  }

  /**
   * Delete group (método legacy)
   */
  deleteGroup(groupName: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}groups/${groupName}`);
  }

  /**
   * Refresh token (método legacy)
   */
  refreshToken(refreshToken: string): Observable<any> {
    console.warn('Refreshing token...');
    const body = { "refresh_token": refreshToken };
    return this.http.post<any>(`${this.baseUrl}refresh-token`, body);
  }
}
